import React, { useEffect, useState } from "react";
import { MessageCircle, Phone, Pencil, Trash2, Bell, FileText, DollarSign, Send, Loader2, StickyNote } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { useCrm } from "../../pages/crm/CrmApp";
import { money, STATUS, QSTATUS, fmtDate, handleWa, preOpen } from "../../lib/crmApi";
import { toast } from "../../hooks/use-toast";
import { ContactForm } from "./ContactForm";
import { ReminderDialog } from "./ReminderDialog";
import { QuoteEditor } from "./QuoteEditor";
import { SaleDialog } from "./SaleDialog";

const QUICK = [
  ["Saludo", (n, org) => `Hola ${n}, soy de ${org}. ¿Tienes un momento para platicar sobre lo que necesitas?`],
  ["Seguimiento", (n) => `Hola ${n}, ¿pudiste revisar la información que te compartí? Quedo atento a tus dudas.`],
  ["Recordatorio de pago", (n) => `Hola ${n}, te recuerdo amablemente tu pago pendiente. ¡Gracias!`],
  ["Gracias por tu compra", (n, org) => `¡Gracias por tu compra, ${n}! En ${org} estamos para servirte.`],
];

export const ContactDetail = ({ id, onClose, onChanged }) => {
  const { api, me } = useCrm();
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [modal, setModal] = useState(null);
  const load = () => api(`/contacts/${id}`).then(setD);
  useEffect(() => {
    setD(null);
    if (id) load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => {
    load();
    onChanged?.();
  };
  const c = d?.contact;
  const first = c?.name.split(" ")[0] || "";

  const addNote = async (type = "nota") => {
    if (!note.trim()) return;
    await api(`/contacts/${id}/notes`, { method: "POST", body: { type, text: note } });
    setNote("");
    load();
  };
  const sendWa = async (text) => {
    if (!text.trim()) return;
    setSending(true);
    const w = preOpen();
    try {
      toast({ title: handleWa(await api(`/contacts/${id}/whatsapp`, { method: "POST", body: { text } }), w) });
      setMsg("");
      load();
    } catch (e) {
      w?.close();
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`¿Eliminar a ${c.name}?`)) return;
    try {
      await api(`/contacts/${id}`, { method: "DELETE" });
    } catch (e) {
      return toast({ title: e.message, variant: "destructive" });
    }
    onChanged?.();
    onClose();
  };
  const doneRem = async (r) => {
    await api(`/reminders/${r.id}/done`, { method: "PUT" });
    load();
  };

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-lg" data-testid="crm-contact-detail">
        {!c ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="pb-10">
            <SheetHeader className="border-b border-slate-100 px-5 pb-5 text-left" style={{ paddingTop: "max(20px, env(safe-area-inset-top))" }}>
              <div className="flex items-start gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg font-bold text-white" style={{ background: STATUS[c.status]?.color }}>{c.name[0]}</span>
                <div className="min-w-0 flex-1">
                  <SheetTitle className="truncate text-lg">{c.name}</SheetTitle>
                  <p className="truncate text-sm text-slate-500">{[c.company, c.phone, c.email].filter(Boolean).join(" · ")}</p>
                  <span className="mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold text-white" style={{ background: STATUS[c.status]?.color }} data-testid="crm-detail-status">{STATUS[c.status]?.label}</span>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {c.phone && <a href={`tel:${c.phone}`} className="flex flex-col items-center gap-1 rounded-xl bg-slate-50 py-2 text-[11px] text-slate-600"><Phone className="h-4 w-4" /> Llamar</a>}
                <button onClick={() => setModal("edit")} className="flex flex-col items-center gap-1 rounded-xl bg-slate-50 py-2 text-[11px] text-slate-600" data-testid="crm-detail-edit"><Pencil className="h-4 w-4" /> Editar</button>
                <button onClick={() => setModal("rem")} className="flex flex-col items-center gap-1 rounded-xl bg-slate-50 py-2 text-[11px] text-slate-600" data-testid="crm-detail-reminder"><Bell className="h-4 w-4" /> Recordar</button>
                <button onClick={remove} className="flex flex-col items-center gap-1 rounded-xl bg-red-50 py-2 text-[11px] text-red-600" data-testid="crm-detail-delete"><Trash2 className="h-4 w-4" /> Borrar</button>
              </div>
            </SheetHeader>

            <section className="space-y-2 border-b border-slate-100 p-5">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-800"><MessageCircle className="h-4 w-4 text-emerald-500" /> WhatsApp {me.org.whatsapp_ready ? <span className="text-[10px] font-normal text-emerald-600">automático</span> : null}</p>
              <div className="flex flex-wrap gap-1.5">
                {QUICK.map(([l, fn]) => (
                  <button key={l} onClick={() => setMsg(fn(first, me.org.name))} className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600" data-testid={`crm-quick-${l}`}>{l}</button>
                ))}
              </div>
              <div className="flex gap-2">
                <textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Escribe un mensaje…" className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" data-testid="crm-wa-text" />
                <button disabled={sending || !msg.trim()} onClick={() => sendWa(msg)} className="flex w-12 items-center justify-center rounded-xl bg-emerald-500 text-white disabled:opacity-40" data-testid="crm-wa-send">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
              </div>
            </section>

            <section className="grid grid-cols-2 gap-2 border-b border-slate-100 p-5">
              <button onClick={() => setModal("quote")} className="flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white" style={{ background: "var(--crm)" }} data-testid="crm-detail-new-quote"><FileText className="h-4 w-4" /> Cotizar</button>
              <button onClick={() => setModal("sale")} className="flex items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white" data-testid="crm-detail-new-sale"><DollarSign className="h-4 w-4" /> Registrar venta</button>
            </section>

            {d.reminders.length > 0 && (
              <section className="border-b border-slate-100 p-5">
                <p className="mb-2 text-sm font-semibold text-slate-800">Recordatorios</p>
                {d.reminders.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="min-w-0 truncate text-slate-700">{r.text} <span className="text-xs text-slate-400">· {fmtDate(r.due_at)}</span></span>
                    <button onClick={() => doneRem(r)} className="text-xs font-semibold text-emerald-600">Hecho</button>
                  </div>
                ))}
              </section>
            )}

            <section className="border-b border-slate-100 p-5">
              <p className="mb-2 text-sm font-semibold text-slate-800">Historial de compras · {money(d.sales.reduce((s, x) => s + x.amount, 0))}</p>
              {!d.sales.length && <p className="text-xs text-slate-400">Sin compras todavía.</p>}
              {d.sales.map((s) => (
                <div key={s.id} className="flex justify-between py-1.5 text-sm" data-testid={`crm-detail-sale-${s.id}`}>
                  <span className="text-slate-700">{s.description || "Venta"} <span className="text-xs text-slate-400">· {fmtDate(s.date)}</span></span>
                  <span className="font-semibold text-slate-900">{money(s.amount)}</span>
                </div>
              ))}
            </section>

            <section className="border-b border-slate-100 p-5">
              <p className="mb-2 text-sm font-semibold text-slate-800">Cotizaciones</p>
              {!d.quotes.length && <p className="text-xs text-slate-400">Sin cotizaciones.</p>}
              {d.quotes.map((q) => (
                <button key={q.id} onClick={() => setModal({ quote: q })} className="flex w-full justify-between py-1.5 text-left text-sm" data-testid={`crm-detail-quote-${q.id}`}>
                  <span className="text-slate-700">{q.number} <span className="text-xs text-slate-400">· {QSTATUS[q.status]}</span></span>
                  <span className="font-semibold text-slate-900">{money(q.total)}</span>
                </button>
              ))}
            </section>

            <section className="p-5">
              <p className="mb-2 text-sm font-semibold text-slate-800">Seguimiento</p>
              <div className="mb-3 flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Agregar nota o resumen de llamada…" className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="crm-note-input" onKeyDown={(e) => e.key === "Enter" && addNote()} />
                <button onClick={() => addNote("nota")} className="rounded-xl bg-slate-900 px-3 text-white" data-testid="crm-note-add"><StickyNote className="h-4 w-4" /></button>
                <button onClick={() => addNote("llamada")} className="rounded-xl bg-slate-100 px-3 text-slate-700" title="Registrar llamada" data-testid="crm-call-add"><Phone className="h-4 w-4" /></button>
              </div>
              <ol className="space-y-3 border-l-2 border-slate-100 pl-4">
                {d.activities.map((a) => (
                  <li key={a.id} className="relative text-sm" data-testid={`crm-activity-${a.id}`}>
                    <span className="absolute -left-[22px] top-1.5 h-2.5 w-2.5 rounded-full" style={{ background: "var(--crm)" }} />
                    <p className="text-slate-700">{a.text}</p>
                    <p className="text-[11px] text-slate-400">{a.type} · {a.user_name} · {fmtDate(a.created_at)}</p>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        )}
        {c && (
          <>
            <ContactForm open={modal === "edit"} initial={c} onClose={() => setModal(null)} onSaved={refresh} />
            <ReminderDialog open={modal === "rem"} contact={c} onClose={() => setModal(null)} onSaved={load} />
            <QuoteEditor open={modal === "quote" || !!modal?.quote} contact={c} quote={modal?.quote} onClose={() => setModal(null)} onSaved={refresh} />
            <SaleDialog open={modal === "sale"} contact={c} quotes={d.quotes} onClose={() => setModal(null)} onSaved={refresh} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
